// The page: load the payload once it's signed in, then hand it to the board.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PipelinePayload } from './api/types.ts';
import { AccessDeniedError, SessionExpiredError, StillBuildingError, fetchPipeline } from './api/client.ts';
import { useAuthContext } from './auth/AuthContext.tsx';
import { DeniedGate, Gate } from './auth/Gate.tsx';
import { pacificToday } from './board/format.ts';
import { Board } from './components/Board.tsx';
import { Masthead } from './components/Masthead.tsx';

// The payload rebuilds twice a day. Coming back to a tab left open longer than this refetches,
// so a board left open across a sync doesn't sit a build behind.
const REFETCH_AFTER_MS = 5 * 60 * 1000;


type LoadState =
  | { kind: 'loading' }
  | { kind: 'building' }
  | { kind: 'loaded'; payload: PipelinePayload; fetched_at: number }
  | { kind: 'denied'; message: string }
  | { kind: 'expired' }
  | { kind: 'failed'; message: string };

function MockBanner() {
  if (!__MOCK_API__) return null;
  return (
    <p className="notice notice-mock">
      <b>Invented data.</b> MOCK_API is on: every account, person and next step on this page is
      made up.
    </p>
  );
}

// Grey blocks where the stage boxes and the rows will be, so the page keeps its shape.
function BoardSkeleton({ message }: { message: string }) {
  return (
    <div className="skeleton" aria-busy="true">
      <p className="skeleton-message">{message}</p>
      <div className="skeleton-stages">{[1, 2, 3, 4, 5].map(key => <span key={key} className="skeleton-block" />)}</div>
      <div className="skeleton-rows">{[1, 2, 3, 4, 5, 6].map(key => <span key={key} className="skeleton-block" />)}</div>
    </div>
  );
}

export function App() {
  const { email, getToken, signOut } = useAuthContext();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  // One load, mapped onto the page's states; refusals, expiry and a first build are answers,
  // not errors. A first build retries itself after the server's Retry-After.
  const retryTimer = useRef<number | null>(null);
  const load = useCallback(() => {
    fetchPipeline(getToken)
      .then(payload => setState({ kind: 'loaded', payload, fetched_at: Date.now() }))
      .catch((error: Error) => {
        if (error instanceof AccessDeniedError) setState({ kind: 'denied', message: error.message });
        else if (error instanceof SessionExpiredError) setState({ kind: 'expired' });
        else if (error instanceof StillBuildingError) {
          setState({ kind: 'building' });
          retryTimer.current = window.setTimeout(() => load(), error.retry_after_seconds * 1000);
        } else setState({ kind: 'failed', message: error.message });
      });
  }, [getToken]);

  useEffect(() => {
    load();
    return () => { if (retryTimer.current !== null) window.clearTimeout(retryTimer.current); };
  }, [load]);

  // Refetch when the tab comes back into view after a while away.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (state.kind === 'loaded' && Date.now() - state.fetched_at < REFETCH_AFTER_MS) return;
      load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load, state]);

  if (state.kind === 'denied') return <DeniedGate message={state.message} email={email} onSignOut={signOut} />;
  if (state.kind === 'expired') {
    return (
      <Gate message="Your session has expired.">
        <button className="button" onClick={() => window.location.reload()}>Sign in again</button>
      </Gate>
    );
  }

  const payload = state.kind === 'loaded' ? state.payload : null;
  return (
    <main className="page">
      <MockBanner />
      {payload ? null : <Masthead salesforceThrough={null} />}
      {state.kind === 'loading' ? <BoardSkeleton message="Loading the pipeline…" /> : null}
      {state.kind === 'building' ? (
        <BoardSkeleton message="Building the board from Salesforce. The first build after a restart takes about a minute; this page will load by itself." />
      ) : null}
      {state.kind === 'failed' ? (
        <div className="notice notice-error error-card" role="alert">
          <b>The pipeline couldn't be loaded.</b>
          <span>{state.message}</span>
          <button type="button" className="button" onClick={() => { setState({ kind: 'loading' }); load(); }}>Try again</button>
        </div>
      ) : null}
      {payload ? <Board payload={payload} today={pacificToday(new Date())} getToken={getToken} /> : null}
    </main>
  );
}
